import { injectable } from 'inversify'
import { isUndefined, omitBy } from 'lodash-es'
// eslint-disable-next-line tp/using-vue-in-services-restriction
import { shallowReactive } from '@/composition/vue/compositionApi'
import type { FormFieldFilterType, SelectValue } from '@/services/forms/types'
import BaseFilterForm from '@/services/tables/filters/baseFilterForm'
import { FILTER_RESERVED_NAMES, type FilterToCreate, type FiltersToCreate } from '@/services/tables/filters/types'
import { FilterField, type FilterFieldData } from '@/services/tables/filters/fields/filterField'
import { FILTER_TYPE_ARRAY, FILTER_TYPE_BASE, type FilterFieldType } from '@/services/tables/filters/fields/types'
import { TmTableFilterError } from '@/core/error/table/tmTableFilterError'

/**
 * Filter definitions are loosely typed by contract: `FilterToCreateCallbackData` carries an index
 * signature, an optional operation arrives as an empty string and options may be a loader function.
 * The payload is therefore shaped by the field defaults rather than by types
 */
export type FilterFieldInitData = Record<string, unknown>

const DEFAULTS_BY_TYPE = new Map<FilterFieldType, () => Partial<FilterFieldData>>([
  [FILTER_TYPE_ARRAY, () => ({ value: [], innerValue: [] })],
])

@injectable()
export class FilterBuilderService {
  public build<T extends FormFieldFilterType, TName extends string = string, R = SelectValue>(
    formId: string,
    filters: FiltersToCreate<TName, R>,
  ): BaseFilterForm<T> {
    const form = new BaseFilterForm<T>(formId)
    form.buildForm(this.createFields(formId, filters) as T[])

    return form
  }

  /**
   * Undefined values are skipped so that a field default wins.
   * Shallow reactivity is enough because every write replaces a whole value, nothing is mutated in place
   */
  public createField(data: FilterFieldInitData): FilterField {
    const filterType = (data.filterType as FilterFieldType) ?? FILTER_TYPE_BASE
    const fieldData = { ...data, options: this.toFieldOptions(data.options) }

    return shallowReactive(
      Object.assign(new FilterField(), DEFAULTS_BY_TYPE.get(filterType)?.(), omitBy(fieldData, isUndefined)),
    )
  }

  public buildFieldId(formId: string, filterName: string) {
    return `${formId}.${filterName}`
  }

  protected createFields<TName extends string, R>(formId: string, filters: FiltersToCreate<TName, R>): FilterField[] {
    const fields: FilterField[] = []

    for (const filter of filters) {
      if (FILTER_RESERVED_NAMES.includes(filter.name)) {
        throw new TmTableFilterError(`"${filter.name}" is reserved and can't be used as filter name`)
      }

      if (filter.disabled) {
        continue
      }

      const operation = this.extractOperation(filter)
      const baseData = {
        id: this.buildFieldId(formId, filter.name),
        formId,
        filterType: filter.filterType,
        name: filter.name,
        queryName: filter.queryName,
        label: filter.label,
        icon: filter.icon,
        availableOperations: filter.availableOperations ?? (filter.operation ? [filter.operation] : []),
        relatedFieldName: filter.relatedFieldName,
        readonly: filter?.readonly ?? false,
        isDefault: filter?.isDefault,
        isHidden: filter?.isHidden ?? false,
        operation,
        innerOperation: operation,
        initialOperation: operation,
        options: [],
        innerSelectedItemsValue: [],
        selectedItemsValue: [],
      }

      fields.push(this.createField(filter.callback ? filter.callback(baseData) : baseData))
    }

    return fields
  }

  /**
   * An optional operation is stored as an empty string, and a filter falls back to the first
   * available operation when the declared one is not among them
   */
  protected extractOperation<TName extends string, R>(filter: FilterToCreate<TName, R>) {
    if (filter.isOptionalOperation && !filter.operation) {
      return ''
    }

    const operation = filter.operation ?? 'in'
    const operations = filter.availableOperations ?? (filter.operation ? [filter.operation] : [])

    return !operations.includes(operation) && operations[0] ? operations[0] : operation
  }

  /**
   * Loadable options are declared as a factory: calling it builds the fetch function together with its
   * own pagination state. A static list of options is passed through
   */
  protected toFieldOptions(options: unknown): unknown {
    return typeof options === 'function' ? (options as () => unknown)() : options
  }
}
